import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { PopoverContent, PopoverControlled, PopoverTrigger } from '@1trading/react/components/ui/popover';
import { PseudoInputButton, PseudoInputIcon, PseudoInputLabel, PseudoInputOptions } from '@1trading/react/components/ui/pseudo-input';
import { ChevronsUpDown } from 'lucide-react';
import { type FieldPathByValue, type FieldValues, type Path, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { InstitutionAddress } from '@/features/institution/schemas/address';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';

type InstitutionFieldsetAddressProps<T extends FieldValues> = {
  name: FieldPathByValue<T, InstitutionAddress>;
  disabled?: boolean;
};

const InstitutionFieldsetAddress = <T extends FieldValues>({ name, disabled }: InstitutionFieldsetAddressProps<T>) => {
  const form = useFormContext<T>();
  const { countriesAsOptions, data: countries } = useMainstreamCountries();
  const { t } = useTranslation();
  const country = form.watch(`${name}.country` as Path<T>);
  const postalCodeUse = (country && countries?.[country]?.postalCodeUse) || 'required';

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-6">
      <FormField
        control={form.control}
        name={`${name}.addressLine1` as Path<T>}
        render={({ field }) => (
          <FormItem className="col-span-2">
            <FormLabel>{t('institutionDetails.address.addressLine1')}*</FormLabel>
            <FormControl>
              <Input {...field} disabled={disabled} placeholder={t('institutionDetails.address.placeholders.addressLine1')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`${name}.addressLine2` as Path<T>}
        render={({ field }) => (
          <FormItem className="col-span-2">
            <FormLabel>{t('institutionDetails.address.addressLine2')}</FormLabel>
            <FormControl>
              <Input {...field} disabled={disabled} placeholder={t('institutionDetails.address.placeholders.addressLine2')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {postalCodeUse !== 'none' && (
        <FormField
          control={form.control}
          name={`${name}.postalCode` as Path<T>}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('institutionDetails.address.postalCode')}
                {postalCodeUse === 'required' ? '*' : ''}
              </FormLabel>
              <FormControl>
                <Input {...field} disabled={disabled} placeholder={t('institutionDetails.address.placeholders.postalCode')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      <FormField
        control={form.control}
        name={`${name}.locality` as Path<T>}
        render={({ field }) => (
          <FormItem className={postalCodeUse === 'none' ? 'col-span-2' : undefined}>
            <FormLabel>{t('institutionDetails.address.locality')}*</FormLabel>
            <FormControl>
              <Input {...field} disabled={disabled} placeholder={t('institutionDetails.address.placeholders.locality')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`${name}.country` as Path<T>}
        render={({ field }) => (
          <FormItem className="col-span-2">
            <FormLabel>{t('institutionDetails.address.country')}*</FormLabel>
            <PopoverControlled>
              {(_, setOpen) => (
                <>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <PseudoInputButton role="combobox" disabled={disabled} value={field.value} {...(!field.value && { 'data-placeholder': '' })}>
                        <PseudoInputLabel>
                          {field.value ? countriesAsOptions.find((option) => option.value === field.value)?.label : t('institutionDetails.address.placeholders.country')}
                        </PseudoInputLabel>
                        <PseudoInputIcon>
                          <ChevronsUpDown />
                        </PseudoInputIcon>
                      </PseudoInputButton>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" stretch>
                    <PseudoInputOptions
                      empty={t('statuses.noResults')}
                      options={countriesAsOptions}
                      placeholder={t('fundamentals.searchCountries')}
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(field.value === value ? '' : value);
                        setOpen(false);
                      }}
                    />
                  </PopoverContent>
                </>
              )}
            </PopoverControlled>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
InstitutionFieldsetAddress.displayName = 'InstitutionFieldsetAddress';

export { InstitutionFieldsetAddress };
